import { 
  Home, 
  LayoutDashboard, 
  BookOpen, 
  GraduationCap, 
  FileText, 
  Plus,
  Lightbulb,
  Library,
  Award,
  Wrench,
  BarChart3,
  ClipboardList,
  Users,
  Settings,
  Target,
  Clock,
  FileCheck,
  Shield,
  Calendar,
  TrendingUp,
  Plug,
  User,
  LogOut,
  Tag,
  Trophy,
  Video,
  MonitorPlay,
  Receipt,
  Building2,
  Eye,
  MessageCircle,
  UserCog,
  Users2,
  Search,
  ChevronDown,
  Sparkles,
  Brain
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { isChatAvailable } from '@/utils/chatAvailability';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import edugagyLogo from '@/assets/edugogy-logo.png';

const mainItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "My Profile", url: "/profile", icon: User },
  { title: "Messages", url: "/messaging", icon: MessageCircle },
  { title: "Support & Help", url: "/support", icon: MessageCircle },
];

const learningItems = [
  { title: "Course Catalog", url: "/course-catalog", icon: BookOpen },
  { title: "My Learning", url: "/my-learning", icon: GraduationCap },
  { title: "Personal Insights", url: "/insights", icon: Sparkles },
  { title: "My Groups", url: "/my-groups", icon: Users2 },
  { title: "My Portfolio", url: "/my-portfolio", icon: User },
  { title: "Video Library", url: "/video-library", icon: Video },
  { title: "Video Assignments", url: "/video-assignments", icon: MonitorPlay },
  { title: "NBCT Coach", url: "/nbct-coaching", icon: Trophy },
  { title: "Badges & Awards", url: "/badges", icon: Award },
  { title: "Transcript", url: "/transcript", icon: FileText },
  { title: "Surveys & Feedback", url: "/surveys", icon: ClipboardList },
];

const courseManagementItems = [
  { title: "Course Proposals", url: "/course-proposals", icon: Plus },
  { title: "Course Builder", url: "/course-builder", icon: Wrench },
  { title: "Tag Management", url: "/tag-management", icon: Tag },
  { title: "Creation Tutorials", url: "/course-tutorials", icon: Lightbulb },
  { title: "Manage My Courses", url: "/my-courses", icon: Users },
];

const adminItems = [
  { title: "Admin Dashboard", url: "/admin-dashboard", icon: BarChart3 },
  { title: "Admin Intelligence", url: "/admin/intelligence", icon: Brain },
  { title: "Manage Users", url: "/manage-users", icon: Users },
  { title: "Walkthrough Assistant", url: "/admin-walkthrough", icon: Eye },
  { title: "Survey Management", url: "/survey-management", icon: Target },
  { title: "KPI Management", url: "/kpi-management", icon: Target },
  { title: "PD Transcripts", url: "/transcript", icon: FileText },
  { title: "Certificate Management", url: "/certificate-management", icon: Award },
  { title: "AI Roster Creator", url: "/roster-creator", icon: FileText },
  { title: "Proposal Queue", url: "/proposal-queue", icon: ClipboardList },
  { title: "Integrations", url: "/integrations", icon: Plug },
  { title: "Integrations Guide", url: "/integrations-guide", icon: Plug },
  { title: "Quarterly Learning Days", url: "/quarterly-learning-days", icon: Calendar },
  { title: "Needs Assessment", url: "/rfp-guide", icon: Target },
];

// Items only for uber admin (matt.monjan@infusedu.com) 
const uberAdminItems = [
  { title: "User Impersonation", url: "/impersonation", icon: UserCog },
  { title: "District Management", url: "/district-management", icon: Building2 },
  { title: "District Usage Analytics", url: "/district-usage-analytics", icon: TrendingUp },
  { title: "Yearly Wrap Up", url: "/yearly-wrap-up", icon: Award },
  { title: "Trial Requests", url: "/trial-requests", icon: ClipboardList },
  { title: "PO Tracker", url: "/purchase-orders", icon: Receipt },
  { title: "Invoice Management", url: "/invoice-management", icon: Receipt },
];

const legalItems = [
  { title: "Terms of Use", url: "/terms-of-use", icon: FileCheck },
  { title: "Privacy Policy", url: "/privacy-policy", icon: Shield },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { isAdmin, isCoordinator, isMentor, isInstructor, role, loading } = useRole();
  const { user, signOut } = useAuth();
  const currentPath = location.pathname;
  const [searchQuery, setSearchQuery] = useState("");
  const unreadCount = useUnreadMessages();

  // Debug logging to see what roles are being detected
  console.log('🎭 Sidebar Role Check:', { 
    userEmail: user?.email,
    role, 
    isAdmin, 
    isCoordinator, 
    isInstructor, 
    isMentor,
    loading 
  });

  const isActive = (path: string) => currentPath === path;
  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-primary/10 text-primary font-medium border-r-2 border-primary" : "hover:bg-muted/50";

  const collapsed = state === "collapsed";
  // Don't show admin sections while loading or if user doesn't have the required role
  const canViewAdminSection = !loading && (isAdmin || isCoordinator || isInstructor);
  const canViewMentorProgram = !loading && (isAdmin || isCoordinator || isMentor);
  const isUberAdmin = user?.email === 'matt.monjan@infusedu.com'; // Only uber admin
  const chatAvailable = isChatAvailable();

  // Filter items based on search
  const filterItems = (items: typeof mainItems) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredMain = filterItems(mainItems);
  const filteredLearning = filterItems(learningItems);
  const filteredCourseManagement = filterItems(courseManagementItems);
  const filteredAdmin = filterItems(adminItems);
  const filteredUberAdmin = filterItems(uberAdminItems);
  const filteredLegal = filterItems(legalItems);

  return (
    <Sidebar className={collapsed ? "w-14" : "w-64"} collapsible="icon">
      <div className="py-1 border-b overflow-hidden">
        {!collapsed ? (
          <img src={edugagyLogo} alt="Edugogy" className="h-36 w-full object-contain scale-[2]" />
        ) : (
          <img src={edugagyLogo} alt="Edugogy" className="h-24 w-full object-contain scale-[2]" />
        )}
      </div>

      <SidebarContent className="pt-2">
        {/* Search Bar */}
        {!collapsed && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>
        )}

        {/* Main - Always expanded */}
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMain.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                      {item.title === "Messages" && unreadCount > 0 && (
                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-[10px] font-bold text-white">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Learning - Collapsible */}
        <Collapsible defaultOpen={true} className="group/collapsible">
          <SidebarGroup>
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                Learning
                {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredLearning.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink to={item.url} className={getNavCls}>
                          <item.icon className="h-4 w-4" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Course Management - Collapsible */}
        <Collapsible defaultOpen={false} className="group/collapsible">
          <SidebarGroup>
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                Course Management
                {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredCourseManagement.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink to={item.url} className={getNavCls}>
                          <item.icon className="h-4 w-4" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Administration Section - Collapsible */}
        {canViewAdminSection && (
          <Collapsible defaultOpen={false} className="group/collapsible">
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                  Administration
                  {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredAdmin.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink to={item.url} className={getNavCls}>
                            <item.icon className="h-4 w-4" />
                            {!collapsed && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Uber Admin Section - Collapsible */}
        {isUberAdmin && uberAdminItems.length > 0 && (
          <Collapsible defaultOpen={false} className="group/collapsible">
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                  Uber Admin
                  {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredUberAdmin.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink to={item.url} className={getNavCls}>
                            <item.icon className="h-4 w-4" />
                            {!collapsed && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                    {/* Chat link with green indicator when available */}
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/admin-chat" className={getNavCls}>
                          <MessageCircle className={`h-4 w-4 ${chatAvailable ? 'text-green-500' : ''}`} />
                          {!collapsed && (
                            <span className={chatAvailable ? 'text-green-500 font-semibold' : ''}>
                              Chat {chatAvailable && '●'}
                            </span>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Mentoring - Collapsible */}
        {canViewMentorProgram && (
          <Collapsible defaultOpen={false} className="group/collapsible">
            <SidebarGroup>
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                  Mentoring
                  {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/mentor-program" className={getNavCls}>
                          <Calendar className="h-4 w-4" />
                          {!collapsed && <span>Mentor Program</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/meeting-tracker" className={getNavCls}>
                          <Clock className="h-4 w-4" />
                          {!collapsed && <span>Meeting Tracker</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/mentoring-reports" className={getNavCls}>
                          <BarChart3 className="h-4 w-4" />
                          {!collapsed && <span>Mentoring Reports</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <NavLink to="/mentor/intelligence" className={getNavCls}>
                          <Brain className="h-4 w-4" />
                          {!collapsed && <span>Mentor Intelligence</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {/* Legal - Collapsible */}
        <Collapsible defaultOpen={false} className="group/collapsible">
          <SidebarGroup>
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                Legal
                {!collapsed && <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />}
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {filteredLegal.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink to={item.url} className={getNavCls}>
                          <item.icon className="h-4 w-4" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={() => signOut()}>
                  <LogOut className="h-4 w-4" />
                  {!collapsed && <span>Sign Out</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        </SidebarContent>
    </Sidebar>
  );
}